import type { RequestError } from '~shared/api/errors';
import { useApi } from '~shared/providers/use-api';

import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ProductValidationError } from './find-product-errors';
import { isProductValidationError } from './find-product-errors';
import type {
  FindProductByParamsResponse,
  ProductApi,
  ProductRequestParams,
} from './product-api';
import { PRODUCT_API_KEY } from './product-api';

export function useGetCategoriesQuery() {
  const productApi = useApi<ProductApi>(PRODUCT_API_KEY);
  return useQuery({
    queryKey: ['get-categories'],
    queryFn: () => {
      return productApi.getCategories();
    },
  });
}

export function useFindProductByParams(
  params: Omit<ProductRequestParams, 'channel' | 'productType'>,
  options: Omit<
    UseQueryOptions<any, Error, FindProductByParamsResponse>,
    'queryKey' | 'queryFn'
  >
) {
  const productApi = useApi<ProductApi>(PRODUCT_API_KEY);

  return useQuery({
    ...options,
    queryKey: ['find-product-by-params', params],
    queryFn: async () => {
      try {
        return await productApi.findProductByParams({
          channel: 'ONLINE',
          productType: 'LOAN',
          ...params,
        });
      } catch (error: any) {
        if (isProductValidationError(error as RequestError)) {
          const validationParams = (error as ProductValidationError).body;

          return {
            maxOrderTotalAmount: validationParams.maxOrderTotalAmount,
            minOrderTotalAmount: validationParams.minOrderTotalAmount,
          };
        }

        throw error;
      }
    },
  });
}
